import { useMutation } from "react-query";
import { axios } from "../../lib/request";
import externalAxios from "axios";
import { createStickerBlob } from "../../lib/stickerify";

const readFileURL = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

export const uploadBlobToSignedMedia = async ({ blob, extension = "png", contentType = "image/png" }) => {
    const response = await axios.get(`${process.env.REACT_APP_API_SIGN_URL}/${extension}`);
    const { data } = response || {};
    const options = { headers: { "Content-Type": contentType } };

    await externalAxios.put(data.signature, blob, options);

    const [uuid, signedExtension] = data.name.split(".");

    return {
        uuid,
        extension: signedExtension,
        mediaUrl: data?.uri || data?.name,
    };
};

export const uploadFileToSignedMedia = async (file) => {
    const fileURL = await readFileURL(file);
    const [ext] = file.name.split(".").reverse();
    const response = await axios.get(`${process.env.REACT_APP_API_SIGN_URL}/${ext.toLocaleLowerCase()}`);
    const { data } = response || {};
    const options = { headers: { "Content-Type": file.type } };

    await externalAxios.put(data.signature, file, options);

    const [uuid, extension] = data.name.split(".");

    return {
        uuid,
        extension,
        mediaUrl: data?.uri || data?.name || fileURL,
    };
};

const createStickerMedia = async (sourceUrl) => {
    const stickerBlob = await createStickerBlob(sourceUrl);

    return uploadBlobToSignedMedia({
        blob: stickerBlob,
        extension: "png",
        contentType: "image/png",
    });
};

export const useUploadImageMutation = ({
    useQuery,
    name,
    type = "photo",
    standalone = false,
    stickerify = false,
    storageMode = "field",
    assetName,
    mediaPrefix,
    replaceExisting = false,
}) => {
    const [collection, setCollection] = useQuery();
    const keyMap = {
        photo: 'standard',
        arrival_image: 'arrival',
    };
    const translateKey = (key) => {
        switch (key) {
            case "ya":
                return "correct";
            case "naw":
                return "incorrect";
            case "challengesenabled":
                return "challenges";
            case "streamenabled":
                return "stream";
            case "leaderboardenabled":
                return "leaderboard";
            case "npcsenabled":
                return "npcs";
            case "reelsenabled":
                return "reels";
            case "rewardsenabled":
                return "rewards";
            case "joinenabled":
                return "join";
            case "notificationsenabled":
                return "notifications";
            case "helpenabled":
                return "help";
            case "customenabled":
                return "custom";
            case "overlay":
                return "sticker";
            case "tour_1":
                return 0;
            case "tour_2":
                return 1;
            case "tour_3":
                return 2;
            case "tour_4":
                return 3;
            default:
                return key;
        }
    };
    const key = keyMap[name] || name;

    const commitMedia = async ({ uuid, extension, mediaUrl }) => {
        if (storageMode === "asset") {
            await setCollection({
                assets: {
                    ...collection.assets,
                    photos: [
                        ...(collection.assets?.photos || []).filter(asset => asset.name !== assetName),
                        {
                            name: assetName,
                            url: mediaUrl,
                        },
                    ],
                },
                [`${mediaPrefix}_media_uuid`]: uuid,
                [`${mediaPrefix}_media_extension`]: extension
                    ? extension.toLocaleLowerCase()
                    : extension,
            });
            return;
        }

        if (storageMode === "legacy") {
            const payloadPrefix = (['watermark'].includes(type) || standalone)
                ? `${name}`
                : `${name}_media`;
            let media = (collection.assets?.[`${type}s`] || []).filter(
                (asset) => asset.name !== translateKey(key)
            );
            let mutationPayload = {};

            if (type === "tour") {
                const index = translateKey(key);
                media = collection.tour[index];

                mutationPayload = {
                    tour: [
                        ...collection.tour.slice(0, index),
                        { ...media, url: mediaUrl },
                        ...collection.tour.slice(index + 1),
                    ],
                };
            } else if (media) {
                mutationPayload = {
                    assets: {
                        ...collection.assets,
                        [`${type}s`]: [...media, { name: translateKey(key), url: mediaUrl }],
                    },
                };
            } else {
                mutationPayload = {
                    [type]: {
                        url: mediaUrl,
                    },
                };
            }

            await setCollection({
                ...mutationPayload,
                [`${payloadPrefix}_uuid`]: uuid,
                [`${payloadPrefix}_extension`]: extension
                    ? extension.toLocaleLowerCase()
                    : extension,
            });
            return;
        }

        // Get existing URLs from the collection
        const existingValue = collection?.[name] || '';
        const existingUrls = existingValue ? existingValue.split(',').filter(url => url.trim()) : [];

        const updatedUrls = replaceExisting
            ? [mediaUrl]
            : [...existingUrls, mediaUrl];

        // Update the collection with comma-delimited URLs
        await setCollection({
            [name]: updatedUrls.join(','),
        });
    };

    const clearMedia = async () => {
        await commitMedia({ uuid: null, extension: null, mediaUrl: '' });
    };

    const { isLoading, mutateAsync } = useMutation(async (file) => {
        const uploadedMedia = await uploadFileToSignedMedia(file);
        const finalMedia = stickerify
            ? await createStickerMedia(uploadedMedia.mediaUrl)
            : uploadedMedia;

        await commitMedia(finalMedia);

        return finalMedia;
    });

    const { isLoading: isGenerating, mutateAsync: createAiImage } = useMutation(async (prompt) => {
        const response = await axios.post(process.env.REACT_APP_API_GENERATE_URL, { prompt, generate_type: 1 });
        const { data } = response || {};
        const sourceUrl = data?.url;
        const finalMedia = stickerify
            ? await createStickerMedia(sourceUrl)
            : {
                uuid: data?.uuid,
                extension: data?.extension,
                mediaUrl: sourceUrl,
            };

        await commitMedia(finalMedia);

        return finalMedia;
    });

    return {
        isLoading,
        isGenerating,
        mutateAsync,
        createAiImage,
        clearMedia,
    };
};
